// Tags Service v2 - Full CRUD operations for user tags with API backend
// Replaces localStorage-based tag service

import { API_CONFIG } from "@/lib/api/config";
import { apiClient } from "@/lib/api/client";

// Tag color palette - must match backend
export const TAG_COLORS = [
  "#ef4444",
  "#f97316",
  "#f59e0b",
  "#eab308",
  "#84cc16",
  "#22c55e",
  "#06b6d4",
  "#3b82f6",
  "#6366f1",
  "#8b5cf6",
  "#d946ef",
  "#ec4899",
] as const;

export type TagColor = (typeof TAG_COLORS)[number];
export type TagSource = "manual" | "auto";
export type TagState = "active" | "suggested" | "hidden";
export type TagOrigin = "manual" | "suggested";

export interface Tag {
  id: string;
  userId?: string;
  name: string;
  color: TagColor;
  createdAt: string;
  updatedAt: string;
  lastUsedAt?: string; // ISO timestamp of last tag application
  source?: TagSource;
  state?: TagState;
  tagOrigin?: TagOrigin;
  score?: number;
  clusterId?: string;
  autoScore?: number;
}

export interface CreateTagInput {
  name: string;
  color: TagColor;
}

export interface UpdateTagInput {
  name?: string;
  color?: TagColor;
}

export interface TagStats {
  [tagId: string]: number; // tagId -> post count
}

export interface SetPostTagsInput {
  tagIds: string[];
  hiddenTagIds?: string[];
}

export interface TagShelfCategory {
  id: string;
  name?: string;
  slug?: string;
  path?: string[];
  root?: string;
  depth?: number;
}

export interface TagShelfPostRef {
  id: string;
}

export interface TagShelf {
  category: TagShelfCategory;
  tagIds?: string[];
  postCount: number;
  posts: TagShelfPostRef[];
}

export interface TagShelvesResponse {
  shelves: TagShelf[];
  count?: number;
}

class TagsService {
  private static instance: TagsService;
  private useV2: boolean;
  private get baseUrl(): string {
    return API_CONFIG.BASE_URL;
  }
  private tagsCache: Map<string, { data: any; timestamp: number }> = new Map();
  private readonly CACHE_DURATION = 60000; // 1 minute cache

  constructor() {
    // Always use v2 since we removed localStorage tagging
    this.useV2 = true;
  }

  static getInstance(): TagsService {
    if (!TagsService.instance) {
      TagsService.instance = new TagsService();
    }
    return TagsService.instance;
  }

  isUsingV2(): boolean {
    return this.useV2;
  }

  getConfig() {
    return {
      version: this.useV2 ? "v2" : "v1",
      baseUrl: this.baseUrl,
    };
  }

  switchVersion(useV2: boolean) {
    localStorage.setItem("ff_tags_v2", useV2.toString());
    window.location.reload();
  }

  clearCache() {
    this.tagsCache.clear();
  }

  private async apiCall<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const authHeaders = await apiClient.getAuthHeaders({ includeApiKey: true });
    const url = `${this.baseUrl}${endpoint}`;

    // Check cache for GET requests
    const cacheKey = `${url}:${JSON.stringify(options.body || {})}`;
    const cached = this.tagsCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION && options.method === "GET") {
      return cached.data as T;
    }

    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...authHeaders,
      ...options.headers,
    };

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Let auth service handle token refresh
          throw new Error("Unauthorized - token expired or invalid");
        }

        let errorDetails = "";
        try {
          const errorBody = await response.text();
          errorDetails = errorBody;
        } catch (e) {
          // Ignore
        }
        throw new Error(`API error: ${response.status} ${response.statusText}. Details: ${errorDetails}`);
      }

      // Handle 204 No Content
      if (response.status === 204) {
        return {} as T;
      }

      // Check if response has content
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        console.warn("Response is not JSON:", contentType);
        return {} as T;
      }

      // Parse JSON safely
      let data;
      try {
        const text = await response.text();
        if (!text || text.trim() === "") {
          console.warn("Empty response body");
          return {} as T;
        }
        data = JSON.parse(text);
      } catch (parseError) {
        console.error("Failed to parse JSON response:", parseError);
        return {} as T;
      }

      // Cache GET requests
      if (options.method === "GET" || !options.method) {
        this.tagsCache.set(cacheKey, { data, timestamp: Date.now() });
      }

      return data;
    } catch (error) {
      console.error(`API call failed: ${url}`, error);
      throw error;
    }
  }

  // Core CRUD operations

  // List all tags for the user
  async listTags(options?: { includeSuggested?: boolean; type?: "tag" | "collection"; includePreview?: boolean; limit?: number; offset?: number }): Promise<Tag[]> {
    if (!this.useV2) {
      console.warn("Tags v2 not enabled, returning empty array");
      return [];
    }

    const params = new URLSearchParams();
    if (options?.includeSuggested) {
      params.set("includeSuggested", "true");
    }
    if (options?.type) {
      params.set("type", options.type);
    }
    if (options?.includePreview) {
      params.set("includePreview", "true");
    }
    if (options?.limit) {
      params.set("limit", options.limit.toString());
    }
    if (options?.offset) {
      params.set("offset", options.offset.toString());
    }
    const query = params.toString();
    const tags = await this.apiCall<unknown>(query ? `/tags?${query}` : "/tags");
    if (!Array.isArray(tags)) {
      throw new Error("Tags API returned a non-array response");
    }
    // Return unsorted - let components decide how to sort
    return tags as Tag[];
  }

  // Create a new tag
  async createTag(input: CreateTagInput): Promise<Tag> {
    if (!this.useV2) {
      throw new Error("Tags v2 API not enabled");
    }

    // Validate color
    if (!TAG_COLORS.includes(input.color)) {
      throw new Error(`Invalid color. Must be one of: ${TAG_COLORS.join(", ")}`);
    }

    // Validate name
    if (!input.name || input.name.length > 50) {
      throw new Error("Tag name must be between 1 and 50 characters");
    }

    return this.apiCall<Tag>("/tags", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  // Get a single tag
  async getTag(tagId: string): Promise<Tag | null> {
    if (!this.useV2) {
      return null;
    }

    try {
      return await this.apiCall<Tag>(`/tags/${tagId}`);
    } catch (error) {
      if (error instanceof Error && error.message.includes("404")) {
        return null;
      }
      throw error;
    }
  }

  // Update a tag
  async updateTag(tagId: string, input: UpdateTagInput): Promise<Tag> {
    if (!this.useV2) {
      throw new Error("Tags v2 API not enabled");
    }

    // Validate color if provided
    if (input.color && !TAG_COLORS.includes(input.color)) {
      throw new Error(`Invalid color. Must be one of: ${TAG_COLORS.join(", ")}`);
    }

    // Validate name if provided
    if (input.name !== undefined && (input.name.length === 0 || input.name.length > 50)) {
      throw new Error("Tag name must be between 1 and 50 characters");
    }

    // Clear cache for this tag and list
    this.clearCache();

    return this.apiCall<Tag>(`/tags/${tagId}`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  }

  // Delete a tag
  async deleteTag(tagId: string): Promise<void> {
    if (!this.useV2) {
      throw new Error("Tags v2 API not enabled");
    }

    if (!tagId || typeof tagId !== "string") {
      throw new Error("Tag ID is required");
    }

    // Clear cache
    this.clearCache();

    await this.apiCall<void>(`/tags/${tagId}`, {
      method: "DELETE",
    });
  }

  // Special endpoints

  // Get tag shelves for home rows
  async getShelves(params: { limit?: number; minPosts?: number; postLimit?: number } = {}): Promise<TagShelf[]> {
    if (!this.useV2) {
      return [];
    }

    try {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.set("limit", params.limit.toString());
      if (params.minPosts) queryParams.set("minPosts", params.minPosts.toString());
      if (params.postLimit) queryParams.set("postLimit", params.postLimit.toString());
      const query = queryParams.toString();
      const url = query ? `/tags/shelves?${query}` : "/tags/shelves";
      const response = await this.apiCall<TagShelvesResponse>(url, { method: "GET" });
      return response.shelves ?? [];
    } catch (error) {
      console.error("Failed to get tag shelves:", error);
      return [];
    }
  }

  // Get tag usage statistics, optionally filtered by search params
  async getTagStats(filters?: {
    routing_class?: string;
    category?: string;
    overall_message_intent?: string;
    platform?: string;
  }): Promise<TagStats> {
    if (!this.useV2) {
      return {};
    }

    try {
      const params = new URLSearchParams();
      if (filters?.routing_class) params.set("routing_class", filters.routing_class);
      if (filters?.category) params.set("category", filters.category);
      if (filters?.overall_message_intent) params.set("overall_message_intent", filters.overall_message_intent);
      if (filters?.platform) params.set("platform", filters.platform);
      const qs = params.toString();
      return await this.apiCall<TagStats>(`/tags/stats${qs ? `?${qs}` : ""}`);
    } catch (error) {
      console.error("Failed to get tag stats:", error);
      return {};
    }
  }

  // Get tag stats filtered to a specific set of post IDs (e.g. search results)
  async getTagStatsForPosts(postIds: string[]): Promise<TagStats> {
    if (!this.useV2 || postIds.length === 0) {
      return {};
    }

    try {
      return await this.apiCall<TagStats>("/tags/stats", {
        method: "POST",
        body: JSON.stringify({ post_ids: postIds }),
      });
    } catch (error) {
      console.error("Failed to get filtered tag stats:", error);
      return {};
    }
  }

  // Get posts for a specific tag (paginated)
  async getPostsForTag(
    tagId: string,
    params?: { cursor?: string; limit?: number },
  ): Promise<{ tag_id: string; post_count: number; post_ids: string[]; cursor?: string; hasMore: boolean }> {
    if (!this.useV2) {
      return { tag_id: tagId, post_count: 0, post_ids: [], hasMore: false };
    }

    try {
      const query = new URLSearchParams();
      if (params?.limit) query.set("limit", String(params.limit));
      if (params?.cursor) query.set("cursor", params.cursor);
      const qs = query.toString();
      return await this.apiCall<{
        tag_id: string;
        post_count: number;
        post_ids: string[];
        cursor?: string;
        hasMore: boolean;
      }>(`/tags/${tagId}/posts${qs ? `?${qs}` : ""}`);
    } catch (error) {
      console.error("Failed to get posts for tag:", error);
      return { tag_id: tagId, post_count: 0, post_ids: [], hasMore: false };
    }
  }

  // Get ALL post IDs for a tag, exhausting pagination
  async getAllPostIdsForTag(tagId: string): Promise<string[]> {
    const allIds: string[] = [];
    let cursor: string | undefined;
    do {
      const response = await this.getPostsForTag(tagId, { cursor, limit: 500 });
      allIds.push(...response.post_ids);
      cursor = response.hasMore ? response.cursor : undefined;
    } while (cursor);
    return allIds;
  }

  // Batch get tags by IDs
  async batchGetTags(tagIds: string[]): Promise<Tag[]> {
    if (!this.useV2 || tagIds.length === 0) {
      return [];
    }

    // Limit to 50 tags per request
    if (tagIds.length > 50) {
      console.warn("Batch get limited to 50 tags, truncating");
      tagIds = tagIds.slice(0, 50);
    }

    try {
      const queryParams = new URLSearchParams();
      queryParams.append("ids", tagIds.join(","));

      return await this.apiCall<Tag[]>(`/tags/batch?${queryParams}`);
    } catch (error) {
      console.error("Failed to batch get tags:", error);
      return [];
    }
  }

  // Post-Tag associations

  // Get all tags for a post
  async getPostTags(postId: string, options?: { includeSuggested?: boolean }): Promise<Tag[]> {
    if (!this.useV2) {
      return [];
    }

    try {
      const params = new URLSearchParams();
      if (options?.includeSuggested) {
        params.set("includeSuggested", "true");
      }
      const query = params.toString();
      const url = query ? `/tags/post/${postId}?${query}` : `/tags/post/${postId}`;
      return await this.apiCall<Tag[]>(url);
    } catch (error) {
      console.error("Failed to get post tags:", error);
      return [];
    }
  }

  // Batch get tags for multiple posts (max 50 per request)
  async getBatchPostTags(postIds: string[]): Promise<Record<string, Tag[]>> {
    if (!this.useV2 || postIds.length === 0) {
      return {};
    }

    // Limit to 50 posts per request (Lambda/DynamoDB limits)
    const MAX_BATCH_SIZE = 50;

    try {
      if (postIds.length > MAX_BATCH_SIZE) {
        // Split into multiple requests if needed
        const chunks: string[][] = [];
        for (let i = 0; i < postIds.length; i += MAX_BATCH_SIZE) {
          chunks.push(postIds.slice(i, i + MAX_BATCH_SIZE));
        }

        // Fetch all chunks in parallel
        const results = await Promise.all(
          chunks.map(chunk =>
            this.apiCall<Record<string, Tag[]>>("/tags/batch", {
              method: "POST",
              body: JSON.stringify({ postIds: chunk }),
            }).catch(err => {
              console.error("Failed to load chunk:", err);
              return {}; // Return empty object for failed chunks
            }),
          ),
        );

        // Filter out null/undefined results and merge
        const validResults = results.filter(r => r && typeof r === "object");
        return validResults.length > 0 ? Object.assign({}, ...validResults) : {};
      }

      const result = await this.apiCall<Record<string, Tag[]>>("/tags/batch", {
        method: "POST",
        body: JSON.stringify({ postIds }),
      });

      // Ensure we always return an object
      return result || {};
    } catch (error) {
      console.error("Failed to batch load tags:", error);
      return {};
    }
  }

  // Set tags for a post (replaces all existing tags)
  async setPostTags(postId: string, tagIdsOrInput: string[] | SetPostTagsInput): Promise<void> {
    if (!this.useV2) {
      throw new Error("Tags v2 API not enabled");
    }

    // Clear cache for post tags
    this.clearCache();

    const payload = Array.isArray(tagIdsOrInput) ? { tagIds: tagIdsOrInput } : tagIdsOrInput;
    await this.apiCall<void>(`/tags/post/${postId}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  }

  // Convenience methods

  // Add a single tag to a post (keeps existing tags)
  async addTagToPost(postId: string, tagId: string): Promise<void> {
    const existingTags = await this.getPostTags(postId);
    const existingIds = existingTags.map(t => t.id);

    if (!existingIds.includes(tagId)) {
      await this.setPostTags(postId, [...existingIds, tagId]);
    }
  }

  // Remove a single tag from a post
  async removeTagFromPost(postId: string, tagId: string): Promise<void> {
    const existingTags = await this.getPostTags(postId);
    const newIds = existingTags.filter(t => t.id !== tagId).map(t => t.id);

    await this.setPostTags(postId, newIds);
  }

  // Get or create a tag by name
  async getOrCreateTag(name: string, color?: TagColor): Promise<Tag> {
    const tags = await this.listTags();
    const existing = tags.find(t => t.name.toLowerCase() === name.toLowerCase());

    if (existing) {
      return existing;
    }

    // Create new tag with random color if not provided
    const tagColor = color || TAG_COLORS[Math.floor(Math.random() * TAG_COLORS.length)];
    return this.createTag({ name, color: tagColor });
  }

  // Search tags by name (client-side filtering)
  async searchTags(query: string): Promise<Tag[]> {
    const tags = await this.listTags();
    const lowerQuery = query.toLowerCase();

    return tags.filter(tag => tag.name.toLowerCase().includes(lowerQuery));
  }

  // Get popular tags (based on usage stats)
  async getPopularTags(limit: number = 10): Promise<Array<Tag & { count: number }>> {
    const [tags, stats] = await Promise.all([this.listTags(), this.getTagStats()]);

    // Combine tags with their counts
    const tagsWithCounts = tags.map(tag => ({
      ...tag,
      count: stats[tag.id] || 0,
    }));

    // Sort by count descending and return top N
    return tagsWithCounts.sort((a, b) => b.count - a.count).slice(0, limit);
  }
}

// Export singleton instance
export const tagsService = TagsService.getInstance();

// Also export class for testing
export default TagsService;
